import React, {Component} from 'react';
import {Link as RouterLink} from 'react-router-dom';
import './TransactionRiskEngineDetails.css';
import transactionRiskAlert from './assets/Transaction-risk-engine Alert Page.png';
import transactionRiskCreate from './assets/Transaction-risk-engine create-transaction.png';
import transactionRiskEngine from './assets/Transaction-risk-engine dashboard.png';
import transactionRiskDecision from './assets/Transaction-risk-engine Risk Decision Page.png';

class TransactionRiskEngineDetails extends Component {
    render() {
        return (
            <main className="projectDetailsPage">
                <section className="projectDetailsHero">
                    <RouterLink className="projectBackLink" to="/">Back to Portfolio</RouterLink>
                    <p className="projectDetailsEyebrow">Featured project</p>
                    <h1>Transaction Risk Engine</h1>
                    <p>
                    An event-driven financial transaction monitoring platform built with Spring Boot, Kafka, PostgreSQL, and React.
                    </p>
                    <div className="projectDetailsActions">
                        <a href="https://github.com/ankitlalwani/transaction-risk-engine" target="_blank" rel="noopener noreferrer">Open Codebase</a>
                    </div>
                </section>

                <section className="projectScreenshotGallery" aria-label="Transaction Risk Engine screenshots">
                    <video
                        className="projectDetailsDemoVideo"
                        controls
                        preload="metadata"
                        poster={transactionRiskEngine}
                    >
                        <source src="/videos/transaction-risk-engine-demo.mp4" type="video/mp4" />
                    </video>
                    <img src={transactionRiskCreate} alt="Create transaction screen" />
                    <img src={transactionRiskDecision} alt="Risk decision screen" />
                    <img src={transactionRiskAlert} alt="Alert review screen" />
                </section>

                <section className="projectDetailsSection">
                    <h2>1. Overview</h2>
                    <p>Transaction Risk Engine is an event-driven financial transaction monitoring platform built with Spring Boot, Kafka, PostgreSQL, and React.</p>
                    <p>The system simulates how financial transactions can be ingested, evaluated for risk, and routed into an alert workflow for analyst review. It uses Kafka-based asynchronous communication between services, a transactional outbox pattern for reliable event publishing, rule-based risk scoring, and a lightweight React dashboard to visualize transactions, risk decisions, and alerts.</p>

                    <div className="serviceGrid">
                        <div>
                            <h3>transaction-ingestion-service</h3>
                            <ul>
                                <li>accepts and validates transactions</li>
                                <li>stores transactions</li>
                                <li>publishes transaction.created.v1</li>
                            </ul>
                        </div>
                        <div>
                            <h3>risk-engine-service</h3>
                            <ul>
                                <li>consumes transaction events</li>
                                <li>applies risk rules</li>
                                <li>calculates risk score and risk level</li>
                                <li>publishes risk.evaluated.v1</li>
                            </ul>
                        </div>
                        <div>
                            <h3>alert-service</h3>
                            <ul>
                                <li>consumes risk evaluation events</li>
                                <li>creates alerts for high-risk transactions</li>
                                <li>supports analyst-style alert review</li>
                            </ul>
                        </div>
                        <div>
                            <h3>risk-dashboard-ui</h3>
                            <ul>
                                <li>provides a lightweight React interface to demo the flow</li>
                            </ul>
                        </div>
                    </div>
                </section>

                <section className="projectDetailsSection">
                    <h2>2. Problem Statement</h2>
                    <p>Financial systems process large volumes of transactions where some activity may require additional review due to fraud, compliance, or operational risk.</p>
                    <p>A common challenge is not just identifying risky transactions, but designing the backend workflow reliably:</p>
                    <div className="questionGrid">
                        <span>How do we ingest transactions safely?</span>
                        <span>How do we publish events without losing them?</span>
                        <span>How do we evaluate risk asynchronously?</span>
                        <span>How do we avoid duplicate processing?</span>
                        <span>How do we create alerts for human review?</span>
                        <span>How do we make the system observable and explainable?</span>
                    </div>
                    <p>A simple synchronous API flow is not enough for this kind of system. Transaction monitoring needs reliable ingestion, event-driven processing, idempotent consumers, auditable decisions, and clear separation between transaction capture, risk evaluation, and alert operations.</p>
                    <p>This project addresses that problem by building a distributed backend workflow that turns raw transaction activity into risk decisions and operational alerts.</p>
                </section>

                <section className="projectDetailsSection">
                    <h2>3. Why I Built It</h2>
                    <p>I built this project to strengthen and demonstrate practical backend system design skills around Java, Spring Boot, Kafka, PostgreSQL, and event-driven architecture.</p>
                    <p>The goal was not to create a basic CRUD application, but to build a realistic financial-domain backend system that includes patterns commonly used in production systems:</p>
                    <ul>
                        <li>transactional outbox</li>
                        <li>Kafka producer/consumer flow</li>
                        <li>idempotent event processing</li>
                        <li>rule-based risk scoring</li>
                        <li>service ownership boundaries</li>
                        <li>alert lifecycle management</li>
                        <li>React-based analyst dashboard</li>
                    </ul>
                    <p>I also wanted the project to align with my interest in backend engineering for financial and risk-monitoring systems. The architecture gives me a strong foundation to later add AI-assisted risk explanations, where deterministic rules produce auditable decisions and AI helps explain those decisions in plain language for analysts.</p>
                </section>
            </main>
        );
    }
}

export default TransactionRiskEngineDetails;
